import type { ReactNode } from 'react';
import Layout from '../../../components/layout/Layout';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import ArticleHero from '../../../components/news/ArticleHero';
import PullQuote from '../../../components/news/PullQuote';
import KeyPointsBox from '../../../components/news/KeyPointsBox';
import CommentBox from '../../../components/news/CommentBox';
import SourceCard from '../../../components/news/SourceCard';
import RelatedCards from '../../../components/news/RelatedCards';

/* ---- 記事内インライン部品（報道メディア型・帯は使わない） ---- */

function SectionHeading({ id, en, children }: { id: string; en: string; children: ReactNode }) {
  return (
    <div id={id} className="scroll-mt-28 mt-14 mb-6">
      <span aria-hidden="true" className="block w-10 h-px bg-[#a38e65] mb-4" />
      <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-2">{en}</span>
      <h2 className="jba-display font-medium text-[#1a2942] text-[24px] sm:text-[28px] leading-[1.4]">{children}</h2>
    </div>
  );
}

function SourceShot({ src, alt, media, url }: { src: string; alt: string; media: string; url: string }) {
  return (
    <figure className="my-9">
      <img src={src} alt={alt} loading="lazy" className="w-full h-auto border border-[#e2ddd2] shadow-[0_3px_20px_rgba(26,41,66,0.09)]" />
      <figcaption className="mt-2.5 text-[13px] text-[#8f7a56] font-['Noto_Serif_JP',serif] border-l-2 border-[#a38e65] pl-3 leading-[1.7]">
        出典: {media}（<a href={url} target="_blank" rel="noopener noreferrer" className="underline decoration-[#a38e65]/50 break-all hover:text-[#1a2942] transition-colors">{url}</a>）
      </figcaption>
    </figure>
  );
}

function Stat({ children }: { children: ReactNode }) {
  return <span className="font-['Playfair_Display',serif] text-[#8f7a56] text-[1.55em] font-bold tracking-tight px-[2px] align-[-0.06em]">{children}</span>;
}

const P = "text-[#2a2a2a] text-[17px] sm:text-[18px] leading-[1.9] font-['Noto_Serif_JP',serif]";

export default function RafflesLondonCampaignPage() {
  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: 'ニュース・お知らせ', href: '/news' },
    { label: 'Raffles London「The Butler Did It」キャンペーン第2章' }
  ];

  const article = {
    category: '業界動向',
    date: '2025年9月17日',
    title: 'Raffles London「The Butler Did It」キャンペーン第2章',
    subtitle: 'ロンドンの歴史的建造物を舞台にした革新的取り組み',
    heroImage: '/images/generated/jba-fs-111-news_raffles-london-campaign.webp',
    lead: 'Raffles Hotels & Resortsが「The Butler Did It」キャンペーンの第2章を発表し、今度はロンドンの歴史的建造物The OWOを舞台に展開しています。英国俳優Henry Golding氏らを起用した本キャンペーンは、執事サービスのハイパーパーソナライゼーションという現代的価値を、映画的アプローチで表現した注目の取り組みです。',
    comment: 'Raffles Londonによる「The Butler Did It」キャンペーン第2章は、執事サービスの現代的進化を見事に表現した傑出した取り組みとして、弊協会としても深く感銘を受けております。特に「ハイパーパーソナライゼーション」という概念は、私どもが追求している「一期一会のおもてなし」の精神と深く共鳴するものです。The OWOという歴史的建造物を舞台にした映画的表現は、執事サービスが単なる職業を超えて、文化的・芸術的価値を持つことを世界に示しています。このような国際的な取り組みが、日本における執事文化の発展と社会的認知の向上に大きく寄与することを期待しており、私どもとしても、伝統と革新を融合させた世界水準の執事サービスの確立に向けて、より一層の努力を重ねてまいります。',
    related: [
      { to: '/news/raffles-butler-campaign', image: '/images/generated/jba-fs-115-news_raffles-london-campaign-v3.webp', aspect: 'aspect-[3/4]', alt: 'Raffles「The Butler Did It」グローバルキャンペーンの関連記事', title: 'Raffles「The Butler Did It」グローバルキャンペーン', desc: '革新的なブランドキャンペーンの第1章' },
      { to: '/news/apple-robot-butler', image: '/images/generated/jba-fs-116-news_raffles-london-campaign-v4.webp', aspect: 'aspect-video', alt: 'Apple ロボットバトラー開発プロジェクトの関連記事', title: 'Apple ロボットバトラー開発プロジェクト', desc: 'AI技術が切り開く執事サービスの未来' }
    ]
  };

  const sources = [
    { label: '出典元', value: 'Raffles Hotels & Resorts（Accor Pressroom 公式発表）', link: false },
    { label: 'キャンペーン報道', value: 'https://globetrender.com/2025/09/17/raffles-unveils-second-fashion-forward-butler-did-it-campaign/', link: true },
    { label: 'Accor プレスルーム', value: 'https://press.accor.com/?p=53198', link: true },
    { label: '公式キャンペーンサイト', value: 'https://www.raffles.com/the-butler-did-it/', link: true },
    { label: 'アクセス日', value: '2026年7月', link: false }
  ];

  const toc = [
    { id: 's-features', label: 'キャンペーン第2章の特徴' },
    { id: 's-cast', label: '豪華キャスト陣' },
    { id: 's-hyper', label: 'ハイパーパーソナライゼーションの実現' },
    { id: 's-owo', label: 'ロンドン舞台の魅力' },
    { id: 's-impact', label: '文化的・社会的インパクト' }
  ];

  return (
    <Layout>
      <ArticleHero eyebrow="News / Industry" category={article.category} title={article.title} subtitle={article.subtitle} date={article.date} image={article.heroImage} />
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-14">
          <div className="border-l-2 border-[#a38e65] pl-6 sm:pl-8">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">Summary</span>
            <p className="jba-display text-[#1a2942] text-lg sm:text-xl leading-[1.95] tracking-[0.01em]">{article.lead}</p>
          </div>
        </div>
      </section>

      <div className="bg-white">
        <div className="max-w-6xl mx-auto px-6 pt-12 pb-4 lg:grid lg:grid-cols-[minmax(0,1fr)_17rem] lg:gap-14">
          <article className="w-full max-w-[760px] mx-auto lg:mx-0">
            <KeyPointsBox
              title="この記事のポイント"
              points={[
                '「The Butler Did It」第2章は2025年9月17日にローンチ。舞台はロンドンのRaffles London at The OWO',
                'バトラー役のTim Eastonが続投し、Henry Golding、Oli Green、Jacquetta Wheeler、Stephanie Grainger、Amalie Gassmanが出演。監督＝Trey Laird、写真＝Dylan Don、スタイリング＝Robert Rabensteiner',
                '見どころには午後のプライベートティー、就寝前のビスポークな物語、国王のバグパイパー（King\'s Piper）による目覚めなど、意外性のあるサービス演出が含まれる'
              ]}
            />

            {/* 01: 第2章の特徴（既存本文・一字も削らず） */}
            <SectionHeading id="s-features" en="Chapter Two">キャンペーン第2章の特徴</SectionHeading>
            <div className="space-y-6">
              <p className={P}>「The Butler Did It」キャンペーンの第2章は、ロンドンの象徴的な歴史的建造物であるThe OWO（Old War Office）を舞台に展開されています。この建物は、かつて英国政府の戦争省として使用されていた由緒ある建造物で、現在はRaffles Londonとして生まれ変わっています。</p>
              <p className={P}>キャンペーンには、「Crazy Rich Asians」で知られる英国俳優Henry Golding氏が出演し、映画的な演出でRafflesの執事サービスの真髄を表現しています。この映像作品は、単なる広告を超えて、執事サービスの芸術性を追求した文化的作品として高く評価されています。</p>
            </div>
            <SourceShot
              src="/images/news-sources/raffles-london-campaign-globetrender.jpg"
              alt="Globetrenderに掲載されたRaffles「The Butler Did It」キャンペーン第2章の報道記事のスクリーンショット。見出しとキャンペーンビジュアルが確認できる"
              media="Globetrender"
              url="https://globetrender.com/2025/09/17/raffles-unveils-second-fashion-forward-butler-did-it-campaign/"
            />

            <div className="my-10 -mx-6 sm:mx-0">
              <PullQuote quote="ロンドンの歴史的建造物The OWOを舞台に、執事サービスの芸術性を映画的アプローチで描く。" cite="Raffles London「The Butler Did It」第2章" />
            </div>

            {/* 増補: 豪華キャスト陣 */}
            <SectionHeading id="s-cast" en="Star Cast">豪華キャスト陣</SectionHeading>
            <div className="space-y-6">
              <p className={P}>第2章ではモデルのTim Eastonがバトラーを再演し、Henry GoldingをはじめOli Green、Jacquetta Wheeler、Stephanie Grainger、Amalie Gassmanら新キャストが登場します。監督はTrey Laird、写真はDylan Don、スタイリングはRobert Rabensteinerが担いました。</p>
              <p className={P}>シンガポール編から続くストーリーがロンドンのThe OWOを舞台に展開され、洗練されたファッションとウィットに富んだサービスを描いています。</p>
            </div>

            {/* 02: ハイパーパーソナライゼーション（既存本文・一字も削らず） */}
            <SectionHeading id="s-hyper" en="Hyper-Personalization">ハイパーパーソナライゼーションの実現</SectionHeading>
            <div className="space-y-6">
              <p className={P}>キャンペーンの中核となるのは、「ハイパーパーソナライゼーション」という概念です。これは、ゲスト一人ひとりの個性、好み、ライフスタイルを深く理解し、それぞれに完全にカスタマイズされた体験を提供するサービス哲学を表しています。</p>
              <p className={P}>Rafflesの執事は、ゲストの到着前から詳細なリサーチを行い、滞在中のあらゆる瞬間において、ゲストが言葉にする前にニーズを察知し、期待を超える体験を創造します。このレベルのサービスは、単なるホスピタリティを超えて、人生の質を向上させる芸術的な領域に達しています。</p>
            </div>

            {/* 増補: ロンドン舞台の魅力 */}
            <SectionHeading id="s-owo" en="The OWO">ロンドン舞台の魅力</SectionHeading>
            <div className="space-y-6">
              <p className={P}>旧陸軍省としてスパイたちが歩いた歴史あるThe OWOの壁を舞台に、午後のプライベートティーや就寝前のビスポークな物語、国王のバグパイパーによる朝の目覚めなど、意外性のあるサービスの瞬間が演出されています。</p>
              <p className={P}>Rafflesのバトラーサービスを英国らしい洗練と神秘性で表現したキャンペーンが、このランドマークを舞台に世界展開されました。</p>
            </div>
            <SourceShot
              src="/images/news-sources/raffles-london-campaign-raffles.jpg"
              alt="Raffles Hotels & Resorts公式サイトの「The Butler Did It」キャンペーンページのスクリーンショット。第2章のキャストと演出が紹介されている"
              media="Raffles Hotels & Resorts 公式サイト"
              url="https://www.raffles.com/the-butler-did-it/"
            />

            {/* 03: 文化的・社会的インパクト（既存本文・一字も削らず） */}
            <SectionHeading id="s-impact" en="Cultural Impact">文化的・社会的インパクト</SectionHeading>
            <div className="space-y-6">
              <p className={P}>このキャンペーンは、執事サービスという伝統的な職業を現代的な文脈で再解釈し、その社会的価値を向上させる重要な役割を果たしています。特に、若い世代に対して執事という職業の魅力と可能性を伝える効果的なメディアとして機能しています。</p>
              <p className={P}>また、ロンドンという歴史的都市を舞台にすることで、英国の執事文化の伝統と現代的革新の融合を象徴的に表現し、国際的な執事サービスの発展に新たな方向性を示しています。</p>
            </div>
          </article>

          <aside className="hidden lg:block">
            <div className="sticky top-28 space-y-8">
              <nav aria-label="目次" className="border border-[#e2ddd2] bg-[#f8f6f0] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Contents</span>
                <ul className="space-y-2.5">
                  {toc.map((t) => (
                    <li key={t.id}>
                      <a href={`#${t.id}`} className="text-[14px] text-[#3a4658] font-['Noto_Serif_JP',serif] leading-[1.6] hover:text-[#8f7a56] transition-colors border-l-2 border-transparent hover:border-[#a38e65] pl-3 -ml-3 block">{t.label}</a>
                    </li>
                  ))}
                </ul>
              </nav>

              <div className="border border-[#e2ddd2] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Article Info</span>
                <dl className="space-y-3 text-[13.5px] font-['Noto_Serif_JP',serif]">
                  <div><dt className="text-[#8f7a56]">発表日</dt><dd className="text-[#333] mt-0.5">2025年9月17日</dd></div>
                  <div><dt className="text-[#8f7a56]">舞台</dt><dd className="text-[#333] mt-0.5">Raffles London at The OWO</dd></div>
                  <div><dt className="text-[#8f7a56]">バトラー役</dt><dd className="text-[#333] mt-0.5">Tim Easton</dd></div>
                  <div><dt className="text-[#8f7a56]">カテゴリ</dt><dd className="text-[#333] mt-0.5">{article.category}</dd></div>
                </dl>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-8 pb-14">
          <CommentBox eyebrow="From the Association" heading="全日本執事協会からのコメント" body={article.comment} />
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pb-14">
          <SourceCard heading="出典" rows={sources} />
        </div>
      </section>

      <RelatedCards heading="関連記事" items={article.related} readLabel="詳しく" backHref="/news" backLabel="ニュース・お知らせ一覧へ戻る" />
    </Layout>
  );
}
